import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Autocomplete,
  Grid,
  Button,
  TextField,
  DialogActions,
  DialogContentText,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from '../../../../redux/store';
import { postVendorIntegrationNoteForm } from '../../../../api/vendors';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

IntegrationNotesModal.propTypes = {
  fetchData: PropTypes.func,
  edit: PropTypes.bool,
  row: PropTypes.object,
  integrationTypes: PropTypes.array,
};

export default function IntegrationNotesModal({ fetchData, edit, row, integrationTypes }) {
  const {
    data: { vendors },
  } = useSelector((state) => state.vendors);
  const [open, setOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState({
    type: '',
    vendor: '',
    description: '',
    primaryContact: '',
    complete: false,
    progressUpdates: [{ message: '' }],
  });

  const handleSave = async () => {
    const response = await postVendorIntegrationNoteForm(value, edit ? row?.id : null);
    if (response) {
      enqueueSnackbar('Note added successfully', { variant: 'success' });
      setOpen(false);
    } else {
      enqueueSnackbar('Failed to add note', { variant: 'error' });
    }
    fetchData();
  };

  const handleClickOpen = () => {
    setValue({
      type: edit ? { id: row.typeId, label: row?.type } : '',
      vendor: edit ? { id: row.vendorId, label: row.vendor } : '',
      description: edit ? row.description : '',
      primaryContact: edit ? row.primaryContact : '',
      complete: edit ? row.complete : false,
      progressUpdates: edit && row.progressUpdates.length > 0 ? row.progressUpdates : [{ message: '' }],
    });
    setOpen(true);
  };
  const handleClose = () => {
    setValue({
      type: '',
      vendor: '',
      description: '',
      primaryContact: '',
      complete: false,
      progressUpdates: [{ message: '' }],
    });
    setOpen(false);
  };

  return (
    <>
      {edit ? (
        <IconButton edge="end" color="secondary" onClick={handleClickOpen} sx={{ mr: 0.5 }}>
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mr: 1 }}
          size="small"
          onClick={handleClickOpen}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          Add Note
        </Button>
      )}

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          {edit ? 'Edit' : 'Add'} Note
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Vendor</DialogContentText>
              <Autocomplete
                size="small"
                fullWidth
                value={value.vendor}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    vendor: newValue,
                  });
                }}
                options={vendors?.map((type) => ({ id: type.id, label: type.name }))}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
              />
            </Grid>
          </Grid>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Type</DialogContentText>
              <Autocomplete
                size="small"
                fullWidth
                value={value?.type}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    type: newValue,
                  });
                }}
                options={integrationTypes?.map((type) => ({ id: type.value, label: type.label }))}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
              />
            </Grid>
          </Grid>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Description</DialogContentText>
              <TextField
                fullWidth
                size="small"
                value={value.description}
                onChange={(e) =>
                  setValue({
                    ...value,
                    description: e.target.value,
                  })
                }
                label=""
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Vendor Contact</DialogContentText>
              <TextField
                fullWidth
                size="small"
                value={value.primaryContact}
                onChange={(e) =>
                  setValue({
                    ...value,
                    primaryContact: e.target.value,
                  })
                }
                label=""
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
                Progress Updates{' '}
                <IconButton
                  edge="end"
                  color="primary"
                  onClick={() => {
                    setValue({
                      ...value,
                      progressUpdates: [...value.progressUpdates, { message: '' }],
                    });
                  }}
                  sx={{ mr: 0.5 }}
                >
                  <Iconify icon="eva:plus-outline" />
                </IconButton>
              </DialogContentText>

              {value.progressUpdates?.map((update, index) => (
                <>
                  <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
                    <Grid item xs={1}>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => {
                          setValue({
                            ...value,
                            progressUpdates: value.progressUpdates.filter((item, i) => i !== index),
                          });
                        }}
                        sx={{ mr: 0.5, mt: 2 }}
                      >
                        <Iconify icon="zondicons:minus-outline" />
                      </IconButton>
                    </Grid>
                    <Grid item xs={11}>
                      <TextField
                        fullWidth
                        size="small"
                        key={index}
                        value={update.message}
                        onChange={(e) => {
                          const newProgressUpdates = [...value.progressUpdates];
                          newProgressUpdates[index].message = e.target.value;
                          setValue({
                            ...value,
                            progressUpdates: newProgressUpdates,
                          });
                        }}
                        label=""
                        sx={{
                          mt: 0.5,
                          '& legend': { display: 'none' },
                          '& fieldset': { top: 0 },
                          '& .MuiFormLabel-root ': { display: 'none' },
                        }}
                        autoComplete="off"
                      />
                    </Grid>
                  </Grid>
                </>
              ))}
            </Grid>
          </Grid>
          <Grid direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value.complete}
                      onChange={(e) =>
                        setValue({
                          ...value,
                          complete: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Complete"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            disabled={value.vendor === '' || value?.type === '' || value.description === ''}
            sx={{ width: 150 }}
            onClick={handleSave}
          >
            {edit ? 'Save' : 'Add Note'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
